import React from "react";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Brush,
} from "recharts";

import RxGhgAccordion from "./RxGhgAccordion";
import { DEFAULT_UNIT } from "../constants";

export default function TrendChartPanel({
  emissionsTrendInReportRange,
  emissionsPeriod,
  setEmissionsPeriod,
  isExporting,
  trendChartRef,
}) {
  const { t } = useTranslation();

  const pointsCount = Array.isArray(emissionsTrendInReportRange)
    ? emissionsTrendInReportRange.length
    : 0;

  const periodLabel = (p) =>
    p === "day"
      ? t("ghg.period.day", "Daily")
      : p === "week"
      ? t("ghg.period.week", "Weekly")
      : t("ghg.period.month", "Monthly");

  return (
    <Box className="rxghg_block">
      <RxGhgAccordion
        id="rxghg_trend"
        title={t("ghg.trend.title", "Avoided emissions over time ({{unit}})", {
          unit: DEFAULT_UNIT,
        })}
        subtitle={t(
          "ghg.common.deliveredWithinReportRange",
          "Delivered requests within the report date range"
        )}
        rightEl={
          <Box
            className="rxghg_accBadges"
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <span className="rxghg_badge">
              {t("ghg.common.pointsCount", "{{count}} points", {
                count: pointsCount,
              })}
            </span>

            <FormControl size="small" className="rxghg_inp" sx={{ minWidth: 140 }}>
              <Select
                value={emissionsPeriod}
                onChange={(e) => setEmissionsPeriod(e.target.value)}
                renderValue={(v) => periodLabel(v)}
              >
                <MenuItem value="day">{t("ghg.period.day", "Daily")}</MenuItem>
                <MenuItem value="week">{t("ghg.period.week", "Weekly")}</MenuItem>
                <MenuItem value="month">{t("ghg.period.month", "Monthly")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        }
      >
        <Box sx={{ height: 340, width: "100%" }} ref={trendChartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={emissionsTrendInReportRange}
              margin={{ top: 10, right: 20, left: 40, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="bucket" tickMargin={8} minTickGap={24} />
              <YAxis
                tickMargin={8}
                width={80}
                label={{
                  value: t("ghg.trend.axisY", "Avoided emissions ({{unit}})", {
                    unit: DEFAULT_UNIT,
                  }),
                  angle: -90,
                  position: "center",
                  dx: -55,
                  style: { fill: "var(--ghg-text, #9aa7b2)" },
                }}
              />

              <RTooltip
                formatter={(value) => [
                  Number(value).toFixed(6),
                  t("ghg.trend.tooltip.series", "Avoided emissions ({{unit}})", {
                    unit: DEFAULT_UNIT,
                  }),
                ]}
                labelFormatter={(label) =>
                  t("ghg.common.tooltip.periodLabel", "Period: {{label}}", {
                    label,
                  })
                }
              />

              <Line
                type="monotone"
                dataKey="avoided"
                name={t("ghg.trend.seriesName", "Avoided emissions ({{unit}})", {
                  unit: DEFAULT_UNIT,
                })}
                stroke="var(--ghg-blue)"
                strokeWidth={2}
                dot={false}
              />

              {!isExporting && <Brush dataKey="bucket" height={24} travellerWidth={10} />}
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.75, mt: 1, display: "block" }}>
          {t(
            "ghg.trend.caption",
            "Each point = Σ [quantity × (emission factor − recycling emissions)] for delivered requests in that period."
          )}
        </Typography>
      </RxGhgAccordion>
    </Box>
  );
}